import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

export default function Register() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (loading) return

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setError('')
    setLoading(true)

    try {
      await register(email, password, displayName)
      // Redirect to check email page — do NOT log in
      navigate('/check-email?email=' + encodeURIComponent(email))
    } catch (err: any) {
      const status = err?.response?.status
      const data = err?.response?.data

      if (status === 429) {
        setError('Too many registration attempts. Please wait 1 hour before trying again.')
      } else if (status === 409) {
        setError('An account with this email already exists. Try signing in instead.')
      } else {
        setError(data?.error || 'Failed to create account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--cream)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        <div
          onClick={() => navigate('/')}
          style={{
            fontFamily: 'var(--font-display)', fontSize: '28px',
            fontWeight: 700, color: 'var(--navy)',
            textAlign: 'center', marginBottom: '40px', cursor: 'pointer',
          }}
        >
          Velu<span style={{ color: 'var(--gold)' }}>mio</span>
        </div>

        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius-lg)',
          padding: '40px', boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--cream-dark)',
        }}>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: '24px',
            marginBottom: '8px', color: 'var(--navy)',
          }}>
            Create your account
          </h2>
          <p style={{
            fontSize: '14px', color: 'var(--muted)', marginBottom: '32px',
          }}>
            Free to start — no credit card required
          </p>

          <form onSubmit={handleSubmit}>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block', fontSize: '13px',
                fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
              }}>
                Your name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="What should we call you?"
                required
                autoComplete="name"
                style={{
                  width: '100%', padding: '12px 14px', fontSize: '14px',
                  border: '1.5px solid var(--warm-border)',
                  borderRadius: 'var(--radius-md)', outline: 'none',
                  fontFamily: 'var(--font-body)', color: 'var(--ink)',
                  background: 'var(--cream)', boxSizing: 'border-box' as const,
                }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block', fontSize: '13px',
                fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
              }}>
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                style={{
                  width: '100%', padding: '12px 14px', fontSize: '14px',
                  border: '1.5px solid var(--warm-border)',
                  borderRadius: 'var(--radius-md)', outline: 'none',
                  fontFamily: 'var(--font-body)', color: 'var(--ink)',
                  background: 'var(--cream)', boxSizing: 'border-box' as const,
                }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block', fontSize: '13px',
                fontWeight: 600, color: 'var(--ink)', marginBottom: '8px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                autoComplete="new-password"
                style={{
                  width: '100%', padding: '12px 14px', fontSize: '14px',
                  border: '1.5px solid var(--warm-border)',
                  borderRadius: 'var(--radius-md)', outline: 'none',
                  fontFamily: 'var(--font-body)', color: 'var(--ink)',
                  background: 'var(--cream)', boxSizing: 'border-box' as const,
                }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--gold)' }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--warm-border)' }}
              />
              <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}>
                Minimum 8 characters
              </p>
            </div>

            {error && (
              <div style={{
                padding: '12px 14px', background: 'var(--rose-light)',
                borderRadius: 'var(--radius-md)', fontSize: '13px',
                color: 'var(--rose)', marginBottom: '20px', lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password || !displayName}
              style={{
                width: '100%', padding: '14px', fontSize: '16px',
                fontWeight: 600, color: 'var(--white)',
                background: loading || !email || !password || !displayName
                  ? 'var(--muted)' : 'var(--navy)',
                border: 'none', borderRadius: 'var(--radius-md)',
                cursor: loading || !email || !password || !displayName
                  ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              {loading ? 'Creating account...' : 'Create free account'}
            </button>

            <p style={{
              fontSize: '11px', color: 'var(--muted)',
              textAlign: 'center', marginTop: '16px', lineHeight: 1.5,
            }}>
              By creating an account you agree to our Terms of Service and Privacy Policy
            </p>

          </form>
        </div>

        <p style={{
          textAlign: 'center', marginTop: '24px',
          fontSize: '14px', color: 'var(--muted)',
        }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--gold)', fontWeight: 500 }}>
            Sign in
          </Link>
        </p>

      </div>
    </div>
  )
}